import { FC } from "react";
import ReactDOM from "react-dom";
import LoadingButton from "../LoadingButton/LoadingButton";
import StyledButton from "../StyledButton/StyledButton";

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: "warning" | "danger" | "info";
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = "warning",
}) => {
  if (!isOpen) return null;

  const getVariantStyles = (): {
    headerBg: string;
    iconColor: string;
    confirmButtonClass: string;
  } => {
    switch (variant) {
      case "danger":
        return {
          headerBg: "bg-red-50",
          iconColor: "text-red-600",
          confirmButtonClass:
            "min-w-[100px] px-6 py-2.5 text-white bg-red-600 rounded-sm hover:bg-red-700 transition-colors",
        };
      case "warning":
        return {
          headerBg: "bg-yellow-50",
          iconColor: "text-yellow-600",
          confirmButtonClass:
            "min-w-[100px] px-6 py-2.5 text-white bg-yellow-600 rounded-sm hover:bg-yellow-700 transition-colors",
        };
      case "info":
      default:
        return {
          headerBg: "bg-blue-50",
          iconColor: "text-blue-600",
          confirmButtonClass:
            "min-w-[100px] px-6 py-2.5 text-white bg-blue rounded-sm hover:bg-blue-700 transition-colors",
        };
    }
  };

  const styles = getVariantStyles();
  const WARNING_ICON = "⚠️";

  return ReactDOM.createPortal(
    <div className="z-[200] fixed inset-0 w-full h-full bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div
          className={`${styles.headerBg} px-6 py-4 border-b border-gray-200`}
        >
          <div className="flex items-center gap-3">
            <span className={`text-2xl ${styles.iconColor}`}>
              {WARNING_ICON}
            </span>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <p className="text-gray-700 leading-relaxed">{message}</p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <StyledButton
            text={cancelText}
            className="min-w-[100px] px-6 py-2.5 text-gray-700 bg-white rounded-sm border border-gray-300 hover:bg-gray-50 transition-colors"
            onClick={onCancel}
          />
          <StyledButton
            text={confirmText}
            className={styles.confirmButtonClass}
            onClick={onConfirm}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmationDialog;
